/**
 * Serviço de Armazenamento Seguro de eBooks
 * Protege acesso através de criptografia e validação de permissões
 */

class SecureEbookStorage {
  /**
   * Chave de criptografia (em produção, deve vir de um servidor seguro)
   */
  ENCRYPTION_KEY = 'secure_ebook_storage_key_v1';
  
  /**
   * Prefixo para diferenciação de dados
   */
  STORAGE_PREFIX = 'secure_ebook_';
  
  /**
   * Tempo de expiração do token de acesso (24 horas)
   */
  TOKEN_EXPIRY = 24 * 60 * 60 * 1000;

  /**
   * Criptografia simples (base64 + salt)
   * NOTA: Em produção, usar biblioteca de criptografia robusta como crypto-js
   */
  encrypt(data) {
    try {
      const jsonString = JSON.stringify(data);
      return btoa(jsonString + '::' + this.ENCRYPTION_KEY);
    } catch (error) {
      console.error('❌ Erro ao criptografar:', error);
      return null;
    }
  }

  /**
   * Descriptografa dados
   */
  decrypt(encryptedData) {
    try {
      const decoded = atob(encryptedData);
      const [jsonString, key] = decoded.split('::');
      
      if (key !== this.ENCRYPTION_KEY) {
        throw new Error('Chave de criptografia inválida');
      }
      
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('❌ Erro ao descriptografar:', error);
      return null;
    }
  }

  /**
   * Gera token de acesso único para um eBook
   */
  generateAccessToken(ebookId, userId) {
    const token = {
      ebookId,
      userId,
      issuedAt: Date.now(),
      expiresAt: Date.now() + this.TOKEN_EXPIRY,
      nonce: Math.random().toString(36).substring(2, 15),
    };
    
    return this.encrypt(token);
  }

  /**
   * Valida token de acesso
   */
  validateAccessToken(token, ebookId, userId) {
    try {
      const decoded = this.decrypt(token);
      
      if (!decoded) {
        console.warn('⚠️ Token inválido ou corrompido');
        return false;
      }
      
      // Verificar expiração
      if (decoded.expiresAt < Date.now()) {
        console.warn('⚠️ Token expirado');
        return false;
      }
      
      // Verificar ebookId e userId
      if (decoded.ebookId !== ebookId || decoded.userId !== userId) {
        console.warn('⚠️ Token não corresponde ao eBook ou usuário');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Erro ao validar token:', error);
      return false;
    }
  }

  /**
   * Armazena eBook de forma segura
   */
  storeEbook(ebookId, ebookData, userId) {
    try {
      // Gerar token de acesso
      const accessToken = this.generateAccessToken(ebookId, userId);
      
      // Estrutura segura do eBook
      const secureEbook = {
        ebookId,
        userId,
        data: ebookData,
        accessToken,
        storedAt: new Date().toISOString(),
        checksum: this.generateChecksum(ebookData),
      };
      
      // Criptografar e armazenar
      const encrypted = this.encrypt(secureEbook);
      const storageKey = `${this.STORAGE_PREFIX}${ebookId}_${userId}`;
      
      localStorage.setItem(storageKey, encrypted);
      
      console.log('✅ eBook armazenado com segurança:', ebookId);
      return {
        success: true,
        ebookId,
        accessToken,
      };
    } catch (error) {
      console.error('❌ Erro ao armazenar eBook:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Recupera eBook de forma segura com validação
   */
  retrieveEbook(ebookId, userId, accessToken) {
    try {
      // Validar token
      if (!this.validateAccessToken(accessToken, ebookId, userId)) {
        console.error('❌ Acesso negado: token inválido');
        return {
          success: false,
          error: 'Token de acesso inválido ou expirado',
          code: 'INVALID_TOKEN',
        };
      }
      
      // Recuperar do armazenamento
      const storageKey = `${this.STORAGE_PREFIX}${ebookId}_${userId}`;
      const encrypted = localStorage.getItem(storageKey);
      
      if (!encrypted) {
        console.error('❌ eBook não encontrado');
        return {
          success: false,
          error: 'eBook não encontrado',
          code: 'NOT_FOUND',
        };
      }
      
      // Descriptografar
      const secureEbook = this.decrypt(encrypted);
      
      if (!secureEbook) {
        console.error('❌ Erro ao descriptografar eBook');
        return {
          success: false,
          error: 'Erro ao descriptografar eBook',
          code: 'DECRYPT_ERROR',
        };
      }
      
      // Validar checksum
      if (!this.verifyChecksum(secureEbook.data, secureEbook.checksum)) {
        console.error('❌ Integridade do eBook comprometida');
        return {
          success: false,
          error: 'Integridade do eBook comprometida',
          code: 'INTEGRITY_ERROR',
        };
      }
      
      console.log('✅ eBook recuperado com segurança:', ebookId);
      
      return {
        success: true,
        ebookId,
        data: secureEbook.data,
        storedAt: secureEbook.storedAt,
      };
    } catch (error) {
      console.error('❌ Erro ao recuperar eBook:', error);
      return {
        success: false,
        error: error.message,
        code: 'RETRIEVAL_ERROR',
      };
    }
  }

  /**
   * Remove eBook de forma segura
   */
  deleteEbook(ebookId, userId) {
    try {
      const storageKey = `${this.STORAGE_PREFIX}${ebookId}_${userId}`;
      localStorage.removeItem(storageKey);
      console.log('✅ eBook removido:', ebookId);
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao remover eBook:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Lista todos os eBooks do usuário de forma segura
   */
  listUserEbooks(userId) {
    try {
      const ebooks = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        
        if (key.startsWith(this.STORAGE_PREFIX)) {
          const encrypted = localStorage.getItem(key);
          const decrypted = this.decrypt(encrypted);
          
          if (decrypted && decrypted.userId === userId) {
            ebooks.push({
              ebookId: decrypted.ebookId,
              storedAt: decrypted.storedAt,
              accessToken: decrypted.accessToken,
            });
          }
        }
      }
      
      return {
        success: true,
        count: ebooks.length,
        ebooks,
      };
    } catch (error) {
      console.error('❌ Erro ao listar eBooks:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Gera checksum para validação de integridade
   */
  generateChecksum(data) {
    try {
      const jsonString = JSON.stringify(data);
      let hash = 0;
      
      for (let i = 0; i < jsonString.length; i++) {
        const char = jsonString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      
      return hash.toString(16);
    } catch (error) {
      console.error('❌ Erro ao gerar checksum:', error);
      return null;
    }
  }

  /**
   * Verifica integridade através do checksum
   */
  verifyChecksum(data, checksum) {
    return this.generateChecksum(data) === checksum;
  }

  /**
   * Limpa todos os eBooks expirados
   */
  cleanupExpiredTokens(userId) {
    try {
      let cleaned = 0;
      
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        
        if (key.startsWith(this.STORAGE_PREFIX)) {
          const encrypted = localStorage.getItem(key);
          const decrypted = this.decrypt(encrypted);
          
          if (decrypted && decrypted.userId === userId) {
            const token = this.decrypt(decrypted.accessToken);
            
            if (token && token.expiresAt < Date.now()) {
              localStorage.removeItem(key);
              cleaned++;
            }
          }
        }
      }
      
      console.log(`✅ ${cleaned} token(s) expirado(s) removido(s)`);
      return { success: true, cleaned };
    } catch (error) {
      console.error('❌ Erro ao limpar tokens expirados:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Audita acessos a eBooks
   */
  logAccess(ebookId, userId, action, success) {
    try {
      const auditLog = {
        ebookId,
        userId,
        action,
        success,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      };
      
      const existing = localStorage.getItem('ebook_audit_log');
      const logs = existing ? JSON.parse(existing) : [];
      
      logs.push(auditLog);
      
      // Manter apenas últimos 1000 logs
      if (logs.length > 1000) {
        logs.shift();
      }
      
      localStorage.setItem('ebook_audit_log', JSON.stringify(logs));
      
      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao registrar auditoria:', error);
      return { success: false };
    }
  }

  /**
   * Obtém logs de auditoria
   */
  getAuditLogs(ebookId = null, userId = null) {
    try {
      const existing = localStorage.getItem('ebook_audit_log');
      let logs = existing ? JSON.parse(existing) : [];
      
      if (ebookId) {
        logs = logs.filter(log => log.ebookId === ebookId);
      }
      
      if (userId) {
        logs = logs.filter(log => log.userId === userId);
      }
      
      return {
        success: true,
        count: logs.length,
        logs,
      };
    } catch (error) {
      console.error('❌ Erro ao recuperar logs:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }
}

export const secureEbookStorage = new SecureEbookStorage();
